import { siretValidator } from "@/components/ui/input-siret";
import { FieldDataType, FieldValidator } from "@/types/dynamic-detail";
import { emailValidator, phoneNumberValidator } from "@/utils/validators";
import { DateTime } from "luxon";
import { z } from "zod";

/** Cross-field date validators that compare against another field's value. */
type CrossFieldDateValidator = Extract<FieldValidator, { type: "date_gte_field" } | { type: "date_lte_field" }>;

/**
 * Coerce a value into an ISO `YYYY-MM-DD` string for safe chronological comparison.
 *
 * Accepts:
 *  - already-ISO strings (`YYYY-MM-DD` or any luxon-parseable ISO datetime),
 *  - the FR display format `dd/MM/yyyy` that may leak through `Field.value` when
 *    `raw_value` is not explicitly configured server-side.
 *
 * Returns `null` if the value cannot be parsed; callers must treat that as
 * "no comparable reference" and skip the check (the server still validates).
 */
export function toIsoDate(input: unknown): string | null {
    if (input == null) return null;
    const str = String(input).trim();
    if (!str) return null;
    let dt = DateTime.fromISO(str);
    if (!dt.isValid) {
        dt = DateTime.fromFormat(str, "dd/MM/yyyy");
    }
    return dt.isValid ? dt.toISODate() : null;
}

/** A form's date as the date picker reads it: ISO (`yyyy-MM-dd`, or a datetime) or French (`dd/MM/yyyy`, as a served
 * `Field.value` may be); nothing when empty or invalid. */
export function pickerDate(value: unknown): DateTime | undefined {
    const iso = toIsoDate(value);
    return iso ? DateTime.fromISO(iso) : undefined;
}

/**
 * Evaluate `date_gte_field` / `date_lte_field` validators for a single date field.
 * Returns the first error message if a constraint fails, or `null` otherwise.
 *
 * The reference value is provided by `getReferenceValue(validator)` so this helper
 * can be reused both inside a zod `superRefine` (read from form `data`) and from
 * an inline-edit context (read from sibling section fields).
 *
 * Comparisons run on parsed ISO dates to avoid lexicographic pitfalls when a
 * caller exposes a display-formatted value (e.g. `05/01/2024`).
 */
export function evaluateDateValidators(
    validators: FieldValidator[] | null | undefined,
    ownValue: unknown,
    getReferenceValue: (validator: CrossFieldDateValidator) => unknown
): string | null {
    if (!validators) return null;
    const ownIso = toIsoDate(ownValue);
    if (!ownIso) return null;
    for (const v of validators) {
        if (v.type !== "date_gte_field" && v.type !== "date_lte_field") continue;
        const refIso = toIsoDate(getReferenceValue(v));
        if (!refIso) continue;
        if (v.type === "date_gte_field" && ownIso < refIso) {
            return v.message ?? `Date must be >= ${refIso}.`;
        }
        if (v.type === "date_lte_field" && ownIso > refIso) {
            return v.message ?? `Date must be <= ${refIso}.`;
        }
    }
    return null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

type BuildZodSchemaOptions = {
    mapFieldSchema?: (params: { field: FieldDataType; schema: z.ZodTypeAny }) => z.ZodTypeAny;
};

function isRequiredField(field: FieldDataType): boolean {
    return field.required === true || field.validators?.some(v => v.type === "required") === true;
}

/** Default for a numeric input: explicit value, else 0 when required, else empty (no cap). */
function defaultNumericFieldValue(field: FieldDataType): number | "" {
    if (field.value != null) return field.value as number;
    return isRequiredField(field) ? 0 : "";
}

/**
 * Build a Zod schema from a list of dynamic fields (from ObjectConfig).
 * Shared between ActionModal and DynamicObjectForm.
 */
export function buildZodSchema(fields: FieldDataType[], options?: BuildZodSchemaOptions) {
    const productSessionStructureSchema = z.object({
        slots: z.array(
            z
                .object({
                    format: z.enum(["fc", "cv"]),
                    duration_minutes: z.number().int().positive(),
                    parts: z.array(z.string().min(1)).optional()
                })
                .superRefine((slot, ctx) => {
                    if (slot.format === "cv" && (!slot.parts || slot.parts.length === 0)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Les slots CV doivent contenir au moins une partie"
                        });
                    }
                    if (slot.format === "fc" && slot.parts && slot.parts.length > 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Le champ parts est réservé aux slots CV"
                        });
                    }
                })
        )
    });

    const shape: Record<string, any> = {};
    fields.forEach(field => {
        const validators = field.validators ?? [];
        const requiredValidator = validators.find(v => v.type === "required");
        const requiredByValidator = validators.some(v => v.type === "required");
        const isRequired = field.required || requiredByValidator;
        const requiredMessage = requiredValidator?.message || `${field.label} est requis`;
        let zodType: any;
        switch (field.field_type) {
            case "email": {
                const invalidEmailMessage = `${field.label} doit être un email valide`;
                if (isRequired) {
                    zodType = z.string().min(1, requiredMessage).refine(emailValidator, invalidEmailMessage);
                } else {
                    // An empty optional email is a valid "clear this field" intent.
                    // `z.string().email()` rejects `""`, which kept the whole form invalid.
                    zodType = z.string().refine(emailValidator, invalidEmailMessage).optional();
                }
                break;
            }
            case "number":
            case "currency":
            case "duration":
            case "percentage":
                zodType = z.coerce.number({ invalid_type_error: `${field.label} doit être un nombre` });
                validators.forEach(validator => {
                    if (validator.type === "min" && validator.value != null) {
                        const message = validator.message || `${field.label} doit être supérieur ou égal à ${validator.value}`;
                        zodType = zodType.min(validator.value, message);
                    }
                    if (validator.type === "max" && validator.value != null) {
                        const message = validator.message || `${field.label} doit être inférieur ou égal à ${validator.value}`;
                        zodType = zodType.max(validator.value, message);
                    }
                });
                if (!isRequired) {
                    // A cleared numeric input reads as `""`, which `z.coerce.number` turns into
                    // `0` — so an optional field declaring `min` could never be left empty, and
                    // one without `min` silently submitted zero instead of no value. Emptiness
                    // is resolved to `null` before coercion, and `null` rather than `undefined`
                    // so that clearing a field that held a value actually clears it server-side
                    // instead of being dropped from the payload.
                    zodType = z.preprocess(value => (value === "" || value === null ? null : value), zodType.nullable().optional());
                }
                break;
            case "checkbox":
            case "boolean":
                zodType = z.boolean();
                if (isRequired) {
                    zodType = zodType.refine((val: any) => val === true, {
                        message: requiredMessage
                    });
                } else {
                    // `null` is « not answered », as a prefill writes it: shown « Non », never an error
                    // (the step would stay blocked behind a field that looks answered).
                    zodType = zodType.nullable().optional();
                }
                break;
            case "json":
                zodType = z.string().superRefine((value, ctx) => {
                    if (!value || !value.trim()) {
                        return;
                    }
                    try {
                        const parsed = JSON.parse(value);
                        if (field.field_name === "product_session_structure") {
                            productSessionStructureSchema.parse(parsed);
                        }
                    } catch (_error) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: field.field_name === "product_session_structure" ? "JSON invalide ou structure de session invalide" : "JSON invalide"
                        });
                    }
                });
                if (isRequired) {
                    zodType = zodType.min(1, requiredMessage);
                } else {
                    zodType = zodType.optional();
                }
                break;
            case "date": {
                // HTML <input type="date"> emits ISO YYYY-MM-DD strings (or "" when cleared).
                const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
                const invalidDateMessage = `${field.label} must be a valid date`;
                if (isRequired) {
                    zodType = z.string().min(1, requiredMessage).refine(v => isoDateRegex.test(v), { message: invalidDateMessage });
                } else {
                    zodType = z.string().refine(v => v === "" || isoDateRegex.test(v), { message: invalidDateMessage }).optional();
                }
                break;
            }
            case "fieldset": {
                // A composite field is its own little form: the same builder, applied to
                // `subfields`, so validators and nested fieldsets need no special case.
                const rowSchema = buildZodSchema(field.subfields ?? [], options);
                if (field.multiple) {
                    zodType = z.array(rowSchema);
                    validators.forEach(validator => {
                        if (validator.type === "min" && validator.value != null) {
                            zodType = zodType.min(validator.value, validator.message || `${field.label} doit contenir au moins ${validator.value} élément(s)`);
                        }
                        if (validator.type === "max" && validator.value != null) {
                            zodType = zodType.max(validator.value, validator.message || `${field.label} doit contenir au plus ${validator.value} élément(s)`);
                        }
                    });
                    if (isRequired) {
                        zodType = zodType.min(1, requiredMessage);
                    } else {
                        zodType = zodType.optional();
                    }
                } else {
                    zodType = isRequired ? rowSchema : rowSchema.optional();
                }
                break;
            }
            case "siret": {
                // A SIRET is 14 digits (SIREN + NIC). Nine digits is a SIREN and is not enough:
                // FIFPL then invents a NIC from the tail of the number.
                const invalidSiretMessage = "Le numéro SIRET doit contenir 14 chiffres";
                if (isRequired) {
                    zodType = z.string().min(1, requiredMessage).refine(siretValidator, invalidSiretMessage);
                } else {
                    zodType = z.string().refine(value => !value || siretValidator(value), invalidSiretMessage).optional();
                }
                break;
            }
            case "key_value":
                zodType = keyValueSchema(field.label, isRequired ? requiredMessage : null);
                break;
            case "tel":
            case "phone":
                // Same rule as the legacy apprenticeship company form: FR metropolitan and DOM-TOM
                // numbers, however the user spaces them. The server normalizes to E.164 on write.
                // `phone` is the CRM FieldType; `tel` is the HTML input alias used by older forms.
                zodType = isRequired ? z.string().min(1, requiredMessage) : z.string();
                zodType = zodType.refine(phoneNumberValidator, `${field.label} n'est pas valide`);
                if (!isRequired) {
                    zodType = zodType.optional();
                }
                break;
            case "alert":
            case "alert-info":
            case "alert-warning":
            case "alert-error":
                return;
            case "select":
            case "combobox":
            case "textarea":
            case "text":
            default: {
                zodType = z.string();
                if (field.max_length) {
                    zodType = zodType.max(field.max_length, `${field.label} doit faire moins de ${field.max_length} caractères`);
                }
                if (isRequired) {
                    zodType = zodType.min(1, requiredMessage);
                } else {
                    zodType = zodType.optional();
                }
                // A `pattern` validator constrains the shape of a value, not its presence:
                // an empty optional field is the required check's business, not this one's.
                // Applied last because `refine` returns an effect, which has no `min`.
                const patternValidator = validators.find(validator => validator.type === "pattern");
                if (patternValidator && patternValidator.type === "pattern") {
                    const expression = new RegExp(`^(?:${patternValidator.pattern})$`);
                    zodType = zodType.refine((value: string | undefined) => !value || expression.test(value), {
                        message: patternValidator.message || `${field.label} n'a pas le format attendu`
                    });
                }
                // An option carries a string, but a form prefilled from the record starts on
                // what the model holds — a relation's id, which `resolve_form_values` reads as
                // a number. The two name the same option, so the number is read as the string
                // the control displays; without that, a record whose every select is filled
                // stays invalid, and on a disabled submit its errors are never even shown.
                // `null` is the other thing a select writes: it is how an option meaning
                // "none" is chosen, and clearing an optional field is not a reason to refuse.
                if (field.field_type === "select" || field.field_type === "combobox") {
                    zodType = z.preprocess(value => (typeof value === "number" ? String(value) : value), isRequired ? zodType : zodType.nullable());
                }
                break;
            }
        }
        shape[field.field_name] = options?.mapFieldSchema ? options.mapFieldSchema({ field, schema: zodType }) : zodType;
    });

    return z.object(shape).superRefine((data, ctx) => {
        fields.forEach(field => {
            const message = evaluateDateValidators(
                field.validators,
                data[field.field_name],
                v => data[v.value_from_field]
            );
            if (message) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [field.field_name],
                    message,
                });
            }
        });
    });
}

/**
 * Turn the string held by every `json` field back into a value, including inside
 * `fieldset` rows. `buildDefaultValues` stringifies those fields so a textarea can
 * edit them, so the payload has to be parsed back before it is submitted.
 *
 * An unparseable value is left as the string it is. A submitted form never gets here
 * with one — the Zod schema refuses invalid JSON first — but a form that writes as it
 * is typed does, on every keystroke between `{` and the closing brace; the raw text
 * then goes out and the server names the field, instead of the render throwing.
 */
export function normalizeJsonFieldValues(fields: FieldDataType[], values: Record<string, unknown>): Record<string, unknown> {
    const result = { ...values };
    fields.forEach(field => {
        const value = result[field.field_name];
        if (field.field_type === "json") {
            if (typeof value === "string") {
                result[field.field_name] = value.trim() ? parseJsonOrKeep(value) : null;
            }
            return;
        }
        if (field.field_type === "key_value") {
            if (Array.isArray(value)) result[field.field_name] = keyValueObject(value as KeyValueRow[]);
            return;
        }
        if (field.field_type !== "fieldset") return;
        const subfields = field.subfields ?? [];
        if (field.multiple) {
            if (Array.isArray(value)) {
                result[field.field_name] = value.map(row => (isPlainObject(row) ? normalizeJsonFieldValues(subfields, row) : row));
            }
        } else if (isPlainObject(value)) {
            result[field.field_name] = normalizeJsonFieldValues(subfields, value);
        }
    });
    return result;
}

function parseJsonOrKeep(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

/**
 * One entry of a `key_value` field, as the form edits it.
 *
 * Rows rather than the object itself, because an object cannot hold what an author holds
 * between two keystrokes: a row whose name is not typed yet, or two rows briefly sharing
 * one. The form keeps the rows; `normalizeJsonFieldValues` folds them back into the object
 * the server stores, in the order they are listed.
 *
 * `value` keeps whatever type it was stored with. A number stays a number until somebody
 * edits it, which is what lets a mapping written years ago round-trip unchanged.
 */
export type KeyValueRow = { key: string; value: unknown };

/** The rows of a stored mapping, in its own order. Rows already are rows. */
export function keyValueRows(value: unknown): KeyValueRow[] {
    if (Array.isArray(value)) return value.filter(isPlainObject).map(row => ({ key: String(row.key ?? ""), value: row.value ?? "" }));
    if (typeof value === "string" && value.trim()) {
        // A mapping that reached the form as JSON text — a legacy default, typically.
        const parsed = parseJsonOrKeep(value);
        return isPlainObject(parsed) ? keyValueRows(parsed) : [];
    }
    if (!isPlainObject(value)) return [];
    return Object.entries(value).map(([key, item]) => ({ key, value: item }));
}

/**
 * The mapping the rows stand for.
 *
 * A row with no name is skipped rather than stored under `""`: it is a row being typed,
 * and the server would otherwise receive a parameter nobody named. A second row with a
 * name already taken is skipped too, so the first one keeps its meaning while the form
 * says the second is a duplicate.
 */
export function keyValueObject(rows: KeyValueRow[]): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    rows.forEach(row => {
        const key = (row?.key ?? "").trim();
        if (!key || key in result) return;
        result[key] = row.value;
    });
    return result;
}

function keyValueSchema(label: string, requiredMessage: string | null) {
    const rowsSchema = z.array(z.object({ key: z.string(), value: z.any() }));
    return (requiredMessage ? rowsSchema.min(1, requiredMessage) : rowsSchema).superRefine((rows, ctx) => {
        const seen = new Set<string>();
        rows.forEach((row, index) => {
            const key = row.key.trim();
            const filled = row.value !== "" && row.value != null;
            if (!key && filled) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, "key"], message: `${label}: row ${index + 1} has a value but no name.` });
            }
            if (key && seen.has(key)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, "key"], message: `${label}: « ${key} » is listed twice; only the first one is kept.` });
            }
            if (key) seen.add(key);
        });
    }).optional();
}

/**
 * First sentence of a description, for the line a form prints where it has no room for
 * the paragraph and keeps the rest on hover.
 *
 * The server writes these texts in full paragraphs, so the first sentence is where the
 * substance is put — which is what makes this a fair thing to show on its own.
 */
export function firstSentence(text: string): string {
    const stop = text.search(/\.\s/);
    return stop < 0 ? text : text.slice(0, stop + 1);
}

/** Field types whose empty input reads as `""` but whose value is not a string. */
const TYPED_FIELDS = new Set(["number", "percentage", "duration", "boolean", "checkbox"]);

/**
 * Replace every value the user left empty by one its own type accepts.
 *
 * A form that submits on a button never needs this: the schema refuses the empty field
 * and nothing is sent. A form that writes **as it is typed** does, because clearing a
 * number box leaves `""` there for as long as it takes to type the next digit, and `""`
 * is not a number — so the write that carried it would be refused and the operator would
 * be told their change was lost for having pressed backspace.
 *
 * What an empty box is worth is read off the descriptor rather than guessed:
 *
 * - a field whose default is not `null` goes back to that default, which is the only
 *   honest reading of "I emptied it" for a value the model always has;
 * - a field that may be absent — an optional field whose default *is* `null` — becomes
 *   `null`, so clearing it really clears it;
 * - a required text becomes `""`, which is what an unfinished text is, and is reported
 *   as a missing value rather than refused.
 *
 * Anything else is passed through untouched: it is better for the server to name the
 * field than for this function to invent a value for it.
 */
export function fillEmptyLeaves(fields: FieldDataType[], values: Record<string, unknown>): Record<string, unknown> {
    const result = { ...values };
    fields.forEach(field => {
        const value = result[field.field_name];

        if (field.field_type === "fieldset") {
            const subfields = field.subfields ?? [];
            if (field.multiple) {
                if (Array.isArray(value)) result[field.field_name] = value.map(row => (isPlainObject(row) ? fillEmptyLeaves(subfields, row) : row));
            } else if (isPlainObject(value)) {
                result[field.field_name] = fillEmptyLeaves(subfields, value);
            }
            return;
        }

        // A `json` field is edited as text and parsed by `normalizeJsonFieldValues`,
        // which already turns an empty one into `null`.
        if (field.field_type === "json") return;
        // A `key_value` field is edited as rows and folded back by `normalizeJsonFieldValues`.
        if (field.field_type === "key_value") {
            if (value == null) result[field.field_name] = [];
            return;
        }
        if (value !== "" && value !== null && value !== undefined) return;

        if (field.value != null) {
            result[field.field_name] = field.value;
            return;
        }
        if (!field.required) {
            result[field.field_name] = null;
            return;
        }
        if (!TYPED_FIELDS.has(field.field_type)) result[field.field_name] = "";
    });
    return result;
}

/**
 * Build default values for a form from a list of dynamic fields.
 * If initialValues is provided, use them to pre-fill the form (update mode).
 */
export function buildDefaultValues(fields: FieldDataType[], initialValues?: Record<string, any>): Record<string, any> {
    return fields.reduce(
        (acc, field) => {
            if (field.field_type === "fieldset") {
                const subfields = field.subfields ?? [];
                const provided = initialValues && field.field_name in initialValues ? initialValues[field.field_name] : field.value;
                if (field.multiple) {
                    // A repeatable fieldset must default to an array: `useFieldArray`
                    // needs one, and each row is normalised like a form of its own.
                    const rows = Array.isArray(provided) ? provided : [];
                    acc[field.field_name] = rows.map(row => buildDefaultValues(subfields, isPlainObject(row) ? row : undefined));
                } else {
                    acc[field.field_name] = buildDefaultValues(subfields, isPlainObject(provided) ? provided : undefined);
                }
                return acc;
            }
            if (field.field_type === "alert" || field.field_type === "alert-info" || field.field_type === "alert-warning" || field.field_type === "alert-error") {
                return acc;
            }
            if (field.field_type === "key_value") {
                const provided = initialValues && field.field_name in initialValues ? initialValues[field.field_name] : field.value;
                acc[field.field_name] = keyValueRows(provided);
                return acc;
            }
            // For update mode: use initialValues if available
            if (initialValues && field.field_name in initialValues) {
                const initial = initialValues[field.field_name];
                if (initial != null) {
                    if (field.field_type === "json") {
                        acc[field.field_name] = typeof initial === "string" ? initial : JSON.stringify(initial, null, 2);
                    } else {
                        acc[field.field_name] = initial;
                    }
                    return acc;
                }
                // A persisted null on an optional numeric field means "no value" (e.g. daily_call_cap).
                if (field.field_type === "number" || field.field_type === "percentage") {
                    acc[field.field_name] = isRequiredField(field) ? 0 : "";
                    return acc;
                }
            }
            // For create mode: use field defaults
            if (field.field_type === "number" || field.field_type === "percentage") {
                acc[field.field_name] = defaultNumericFieldValue(field);
            }
            else if (field.field_type === "checkbox" || field.field_type === "boolean") acc[field.field_name] = field.value || false;
            else if (field.field_type === "json") acc[field.field_name] = field.value ? JSON.stringify(field.value, null, 2) : "";
            else acc[field.field_name] = field.value || "";
            return acc;
        },
        {} as Record<string, any>
    );
}

/** The values chosen in a field, whether kept as a list or as a comma-separated string (a `multiple` combobox). */
export function chosenValues(value: unknown): string[] {
    const items = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : value == null || value === "" ? [] : [value];
    return items.map(item => String(item).trim()).filter(item => item !== "");
}

/**
 * The rows of a repeatable fieldset that follows another field (`rows_from`): one per value chosen,
 * in the order chosen, keyed by `rowKey`. A row already typed for a value is kept as it is; a value
 * newly chosen gets a fresh row (`buildDefaultValues` of the subfields); a row whose value was
 * dropped goes. Returns `rows` itself when nothing changes, so a caller can skip the write.
 */
export function followRows(rows: unknown, chosen: string[], rowKey: string, subfields: FieldDataType[]): Record<string, any>[] {
    const current = Array.isArray(rows) ? rows.filter(isPlainObject) : [];
    const byValue = new Map<string, Record<string, any>>();
    current.forEach(row => {
        const key = row[rowKey] == null ? "" : String(row[rowKey]);
        if (key !== "" && !byValue.has(key)) byValue.set(key, row);
    });
    const next = chosen.map(value => byValue.get(value) ?? { ...buildDefaultValues(subfields), [rowKey]: value });
    const same = Array.isArray(rows) && rows.length === next.length && next.every((row, index) => row === rows[index]);
    return same ? (rows as Record<string, any>[]) : next;
}

/**
 * The server errors a row of a fieldset following another field (`rows_from`) shows. The server files
 * them under the row's key (`dates.<row key>.start_date`), not its position, so that a row moved or
 * dropped since keeps its own message: they are put back at the row's form path (`dates.<index>.…`).
 * Errors of the other rows are left out; errors outside the fieldset are kept as they are.
 */
export function rowErrorsByKey(errors: Record<string, string>, name: string, rowKey: string, index: number): Record<string, string> {
    const prefix = `${name}.`;
    const own = `${name}.${rowKey}.`;
    const result: Record<string, string> = {};
    Object.entries(errors).forEach(([key, message]) => {
        if (key.startsWith(own)) result[`${name}.${index}.${key.slice(own.length)}`] = message;
        else if (!key.startsWith(prefix)) result[key] = message;
    });
    return result;
}

/** Errors per field of a refused action (`field_errors`, else `missing_fields`), one message each. */
export function actionFieldErrors(body: { field_errors?: Record<string, string[] | string>; missing_fields?: string[] } | null | undefined): Record<string, string> {
    const errors: Record<string, string> = {};
    Object.entries(body?.field_errors ?? {}).forEach(([name, messages]) => {
        const text = Array.isArray(messages) ? messages.join(" ") : String(messages);
        if (name !== "__all__" && text) errors[name] = text;
    });
    (body?.missing_fields ?? []).forEach(name => {
        if (!errors[name]) errors[name] = "This field is required.";
    });
    return errors;
}

/**
 * A value the server answers for a field (`form-step` values) as the form holds it: a multiple combobox keeps
 * its choices as one comma-separated string, so a list of plain values is joined; a field the form holds as a
 * list (a `rows_from` fieldset), or a list of rows, stays a list, an empty one included.
 */
export function formValueOf(value: unknown, current?: unknown): unknown {
    if (!Array.isArray(value) || Array.isArray(current)) return value;
    if (value.every(item => item === null || typeof item !== "object")) return value.join(",");
    return value;
}

/** A refusal as one sentence, whatever its shape (`error` of an action or form answer): a React child must never be
 * an object. */
export function errorText(error: string | Record<string, string[] | string> | null | undefined): string | null {
    if (!error) return null;
    if (typeof error === "string") return error;
    return Object.values(error)
        .flatMap(messages => (Array.isArray(messages) ? messages : [messages]))
        .join(" ");
}
